import { use, useEffect, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';

import useAlert from '../Alerts/useAlert';
import MultiselectBox from '../Components/MultiselectBox';
import { UserPreferencesContext } from '../Contexts/UserPreferencesContext';
import { BusinessUnit } from '../Types/BusinessUnit';
import { Line } from '../Types/Line';
import { client } from '../Utilities/AuthUtils';
import { useBusinessUnits } from '../Utilities/useBusinessUnits';
import { useHttpUtils } from '../Utilities/useHttpUtils';
import { useLines } from '../Utilities/useLines';

function UserPreferences() { 
    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const account = client.getAllAccounts()[0];

    const [loading, setLoading] = useState(false);
    const { businessUnits } = useBusinessUnits();
    const { userPreferences } = use(UserPreferencesContext);
    const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<BusinessUnit[]>(businessUnits.filter(bu => userPreferences?.lotList?.defaultBusinessUnits?.some(code => code === bu.code)));
    const [use24hr, setUse24hr] = useState<boolean>(userPreferences?.use24hr ?? false)
    const [locale, setLocale] = useState<string>(userPreferences?.locale ?? "")

    const { lines } = useLines(selectedBusinessUnits);
    const [selectedLines, setSelectedLines] = useState<Line[]>(lines.filter(l => userPreferences?.lotList?.defaultLines?.some(line => line === l.productionLine)));

    useEffect(() => {
        setUse24hr(userPreferences?.use24hr ?? false);
        setLocale(userPreferences?.locale ?? "");
    }, [ userPreferences ]);

    useEffect(() => {
        setSelectedBusinessUnits(businessUnits.filter(bu => userPreferences?.lotList?.defaultBusinessUnits?.some(code => code === bu.code)))
    }, [businessUnits, userPreferences?.lotList?.defaultBusinessUnits])

    useEffect(() => {
        setSelectedLines(lines.filter(l => userPreferences?.lotList?.defaultLines?.some(line => line === l.productionLine)))
    }, [lines, userPreferences?.lotList?.defaultLines])

    async function saveChanges() {
        setLoading(true);

        let updatedUserPreferences = { ...userPreferences };
        updatedUserPreferences.lotList = updatedUserPreferences.lotList ?? {};
        updatedUserPreferences.lotList.defaultBusinessUnits = selectedBusinessUnits.map(bu => bu.code);
        updatedUserPreferences.lotList.defaultLines = selectedLines.map(l => l.productionLine);
        updatedUserPreferences.use24hr = use24hr;
        updatedUserPreferences.locale = locale;

        let body = {
            data: updatedUserPreferences
        };

        await fetchDataAuthenticated("api/v1/userpreferences/" + account.username, "PUT", JSON.stringify(body))
            .then(async response => {
                if (response.ok) {
                    return;
                }

                throw new Error(await response.json());
            })
            .then(data => {
                setAlert("success", "Success", "Saved user preferences");
            })
            .catch(error => {
                setAlert("danger", "Error", "Failed to save user preferences. " + error.message);
            })
            .finally(() => {
                setLoading(false);
            })
    }

    return loading
        ? <div className="d-flex justify-content-center">
            <Spinner animation="border" />
        </div>
        : <Container className="pb-5">
            <Row>
                <Col>
                    <h3>{t<string>('UserPreferences.Title')}</h3>
                </Col>
                <Col>
                    <p className="float-end">{account.username}</p>
                </Col>
            </Row>

            <Container className="p-3 mb-3 bg-light rounded-3">
                <Row className="mb-3">
                    <Col sm={2}>
                        {t<string>('UserPreferences.BusinessUnits')}
                    </Col>
                    <Col sm={8}>
                        <MultiselectBox
                            id={"multiselect-businessUnits"}
                            options={businessUnits}
                            selectedOptions={selectedBusinessUnits}
                            setSelectedOptions={setSelectedBusinessUnits}
                            map="code" />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col sm={2}>
                        {t<string>('UserPreferences.Lines')}
                    </Col>
                    <Col sm={8}>
                        <MultiselectBox
                            id={"multiselect-lines"}
                            options={lines}
                            selectedOptions={selectedLines}
                            setSelectedOptions={setSelectedLines}
                            map="name" />
                    </Col>
                </Row>
                <Row>
                    <Col sm={2}>
                        {t<string>('UserPreferences.Use24hr')}
                    </Col>
                    <Col sm={8}>
                        <Form.Check // prettier-ignore
                            type="switch"
                            id="use24hrSwitch"
                            checked={use24hr ?? false}
                            onChange={(e) => { setUse24hr(e.currentTarget.checked) }}
                            aria-label={t<string>('UserPreferences.Use24hr')}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm={2}>
                        {t<string>('UserPreferences.Locale')}
                    </Col>
                    <Col sm={2}>
                        <Form.Select aria-label="User locale"
                            id="userLocale"
                            value={locale ?? ""}
                            onChange={(e) => { setLocale(e.target.value) }}>
                            <option value="">Not set</option>
                            <option value="en-US">US</option>
                            <option value="en-GB">UK</option>
                            <option value="zh-CN">China</option>
                        </Form.Select>

                    </Col>
                </Row>
                <Row>
                    <Col sm={12}>
                        <Button className="float-end" variant="primary" onClick={() => saveChanges()}>
                            {t<string>('Application.Controls.Save')}
                        </Button>
                    </Col>
                </Row>
            </Container>
        </Container>
}

export default UserPreferences;